import { Autocomplete, type Item, LinkButton } from '@ads/components-react';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/shared/ui/atoms';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { useTutorClassSearch } from '../../../hooks/useTutorClassSearch';
import type { TutorUsageFilters } from '../../../types/tutorAi';

export interface UsageFiltersProps {
  readonly filters: TutorUsageFilters;
  readonly onDateStartChange: (value: string) => void;
  readonly onDateEndChange: (value: string) => void;
  readonly onClassChange: (classStudentId: string, classStudentName: string) => void;
  /**
   * Limpa os filtros, inclusive a seleção de turma — mesmo na aba em que `showClassFilter`
   * é `false` e o campo correspondente está oculto, já que os filtros são compartilhados
   * entre as abas.
   */
  readonly onClear: () => void;
  /**
   * Exibe o recorte por turma. A aba de consumo agregado passa `false`: o endpoint dela
   * não aceita `classStudentId`, e um controle inerte afirmaria um recorte que não é
   * aplicado.
   */
  readonly showClassFilter?: boolean;
}

/**
 * Barra de filtros do dashboard de uso: período e turma.
 *
 * Turma é um autocomplete com busca server-side no lookup compartilhado, seguindo o filtro
 * de turmas de notificações. Campo vazio significa "sem recorte" — é por isso que não
 * existe uma opção "todas" na lista: limpar o campo já é isso, e o placeholder comunica o
 * comportamento.
 *
 * Não há filtro de curso: o backend cria uma turma por curso, então a turma já é o recorte
 * mais fino que existe e um filtro de curso apenas duplicaria a seleção.
 *
 * O recorte por turma é opcional (`showClassFilter`) porque nem toda aba da tela o suporta
 * — ver a prop.
 */
export function UsageFilters({
  filters,
  onDateStartChange,
  onDateEndChange,
  onClassChange,
  onClear,
  showClassFilter = true,
}: UsageFiltersProps) {
  const { t } = useTranslation('tutorAI');
  const { t: tCommon } = useTranslation('common');

  const [classSearch, setClassSearch] = useState('');

  const { items: classItems, isQuerySuccess: isClassSuccess } = useTutorClassSearch(classSearch);

  const classNotFoundMessage = getAutocompleteSearchMessage(
    classSearch.length,
    isClassSuccess,
    classItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const selectedClass: Item<string> | undefined = filters.classStudentId
    ? { label: filters.classStudentName, value: filters.classStudentId }
    : undefined;

  return (
    <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1">
      <div
        className={`grid grid-cols-1 gap-mini sm:grid-cols-2${showClassFilter ? ' xl:grid-cols-3' : ''}`}
      >
        <DatePicker
          label={t('usage.filters.dateStart')}
          value={filters.dateStart}
          onChange={onDateStartChange}
          size="md"
        />
        <DatePicker
          label={t('usage.filters.dateEnd')}
          value={filters.dateEnd}
          onChange={onDateEndChange}
          size="md"
        />

        {showClassFilter ? (
          <Autocomplete<string>
            label={t('usage.filters.class')}
            placeholder={t('usage.filters.classPlaceholder')}
            size="md"
            list={classItems as Item<string>[]}
            custom={{ optionList: 'z-[100]', option: '!h-auto py-nano', input: '!w-full' }}
            {...(classNotFoundMessage !== undefined
              ? { notFoundMessage: classNotFoundMessage }
              : {})}
            {...(selectedClass !== undefined ? { selectedItem: selectedClass } : {})}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setClassSearch(event.target.value)}
            handleChange={(item: Item<string> | null) => {
              const isCleared = !item?.value;
              if (isCleared) setClassSearch('');
              onClassChange(item?.value ?? '', item?.label ?? '');
            }}
          />
        ) : null}
      </div>

      <div className="flex justify-end">
        <LinkButton
          hierarchy="secondary"
          size="sm"
          onClick={() => {
            setClassSearch('');
            onClear();
          }}
        >
          {t('usage.filters.clear')}
        </LinkButton>
      </div>
    </div>
  );
}
